import { useCallback, useEffect } from 'react';

import { useSnackbar } from 'notistack';

import socket from 'services/socket';
import { useAppDispatch } from 'store';
import {
  AtendimentoStatusTransferenciaTypes,
  AtendimentoTransferenciaInterface,
  checkAtendimentoTransferencias,
  fetchAtendimentosIniciados,
} from 'store/reducers/atendimentos';
import { getUrlParams } from 'utils/getUrlParams';

/**
 * Transferências — versão com transferência direta (sem aceite manual).
 *
 * O back-end efetiva a transferência imediatamente ao receber a requisição,
 * portanto não há mais necessidade de:
 *  - Drawer com lista de transferências pendentes para o destinatário aceitar/recusar
 *  - Modal de "Transferência Enviada" aguardando resposta do destinatário
 *  - Modal de "Transferência Recebida" com botões de aceitar/recusar
 *
 * O componente mantém o handler de socket para:
 *  - Atualizar a lista de atendimentos iniciados do destinatário ao receber o evento PENDENTE
 *  - Tratar eventos legados (ACEITO, RECUSADO, CANCELADO) via checkAtendimentoTransferencias
 *    para manter consistência do estado Redux em possíveis cenários de borda
 */
export default function Transferencias() {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { orderInitiatedBy } = getUrlParams();

  // Exibe notificação quando uma transferência de equipe é recebida automaticamente
  const showSnackBarTransferenciaRecebida = useCallback(() => {
    enqueueSnackbar('Você recebeu um novo atendimento por transferência');
  }, [enqueueSnackbar]);

  // Escuta os eventos de transferências
  useEffect(() => {
    const handler = (data: {
      status: AtendimentoStatusTransferenciaTypes;
      transferencia: AtendimentoTransferenciaInterface;
    }) => {
      const { status, transferencia } = data;

      // Mantém o estado Redux sincronizado para cenários de borda
      dispatch(checkAtendimentoTransferencias({ status, transferencia }));

      switch (status) {
        case 'PENDENTE':
          // Transferência direta: atualiza a lista de iniciados imediatamente,
          // sem exibir drawer ou modal de aceite para o destinatário
          dispatch(fetchAtendimentosIniciados({ orderBy: orderInitiatedBy }));
          showSnackBarTransferenciaRecebida();
          break;
        case 'ACEITO':
          // Evento legado mantido por compatibilidade
          dispatch(fetchAtendimentosIniciados({ orderBy: orderInitiatedBy }));
          break;
        case 'RECUSADO':
        case 'CANCELADO':
          // Eventos legados mantidos por compatibilidade
          break;
        default:
          break;
      }
    };

    socket.on('atendimentoTransferencia', handler);

    return () => {
      socket.off('atendimentoTransferencia', handler);
    };
  }, [dispatch, orderInitiatedBy, showSnackBarTransferenciaRecebida]);

  // Nenhum elemento visual é renderizado — a notificação via snackbar é suficiente
  return null;
}
